import { useEffect, useRef, useState } from 'react';
import { isReadableAssetUrl } from './usePreviewAudio';

/**
 * Small frames of the source for the timeline: a hidden <video> that can be read
 * (PithPlay's asset links allow the app's origin) seeks to each wanted time and
 * draws it into a canvas. Frames are cached by time (rounded to a quarter second)
 * and made one at a time, so the timeline fills in as they come. Where the file
 * can't be read, there are simply no thumbnails.
 */

const THUMB_WIDTH = 160;
const STEP_MS = 250;

export const thumbKey = (sourceMs: number): number => Math.max(0, Math.round(sourceMs / STEP_MS) * STEP_MS);

export function useThumbnails(url: string | null, wantedSourceMs: number[]): Record<number, string> {
  const [frames, setFrames] = useState<Record<number, string>>({});
  const state = useRef({
    frames: {} as Record<number, string>,
    queue: [] as number[],
    busy: false,
    broken: false,
    alive: true,
    video: null as HTMLVideoElement | null,
    url: null as string | null,
  });
  const wantedKey = wantedSourceMs.map(thumbKey).join(',');

  useEffect(() => {
    const s = state.current;
    s.alive = true;
    return () => {
      s.alive = false;
      if (s.video) {
        s.video.removeAttribute('src');
        s.video.load();
      }
      // Forget the element, so a remount (React's development double-mount does one)
      // starts a fresh one instead of waiting on an element that no longer loads.
      s.video = null;
      s.url = null;
      s.queue = [];
      s.busy = false;
    };
  }, []);

  useEffect(() => {
    const s = state.current;
    if (typeof document === 'undefined') return;
    // A new source: start over with a fresh element.
    if (s.url !== url) {
      if (s.video) {
        s.video.removeAttribute('src');
        s.video.load();
      }
      s.video = null;
      s.url = url;
      s.frames = {};
      s.queue = [];
      s.busy = false;
      s.broken = false;
      setFrames({});
    }
    if (!url || !isReadableAssetUrl(url) || s.broken) return;
    s.queue = Array.from(new Set(wantedKey ? wantedKey.split(',').map(Number) : [])).filter((ms) => s.frames[ms] === undefined);

    if (!s.video) {
      const el = document.createElement('video');
      el.crossOrigin = 'anonymous';
      el.muted = true;
      el.preload = 'auto';
      el.playsInline = true;
      el.addEventListener('error', () => { s.broken = true; s.queue = []; s.busy = false; });
      el.addEventListener('loadedmetadata', () => pump(s, el, setFrames));
      el.src = url;
      s.video = el;
    }
    if (s.video.readyState >= 1) pump(s, s.video, setFrames);
  }, [url, wantedKey]);

  return frames;
}

type ThumbState = {
  frames: Record<number, string>;
  queue: number[];
  busy: boolean;
  broken: boolean;
  alive: boolean;
  video: HTMLVideoElement | null;
};

/** Makes the next wanted frame, then the one after, until the queue is empty. */
function pump(s: ThumbState, video: HTMLVideoElement, publish: (frames: Record<number, string>) => void): void {
  if (!s.alive || s.busy || s.broken || s.video !== video) return;
  let ms = s.queue.shift();
  while (ms !== undefined && s.frames[ms] !== undefined) ms = s.queue.shift();
  if (ms === undefined) return;
  const wanted = ms;
  s.busy = true;
  const done = () => {
    video.removeEventListener('seeked', done);
    if (s.video !== video) return;
    try {
      const width = THUMB_WIDTH;
      const height = Math.max(1, Math.round((video.videoHeight / Math.max(1, video.videoWidth)) * width));
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const context = canvas.getContext('2d');
      if (context) {
        context.drawImage(video, 0, 0, width, height);
        s.frames = { ...s.frames, [wanted]: canvas.toDataURL('image/jpeg', 0.6) };
        if (s.alive) publish(s.frames);
      }
    } catch {
      // A file that can't be read (no CORS): no thumbnails at all, rather than a broken half.
      s.broken = true;
      s.queue = [];
    }
    s.busy = false;
    pump(s, video, publish);
  };
  video.addEventListener('seeked', done);
  const target = Math.min(wanted / 1000, Math.max(0, (video.duration || wanted / 1000) - 0.05));
  if (Math.abs(video.currentTime - target) < 0.001 && video.readyState >= 2) done();
  else video.currentTime = target;
}

/** The nearest made frame to a source time, if any is close (within two seconds). */
export function nearestFrame(frames: Record<number, string>, sourceMs: number): string | null {
  const exact = frames[thumbKey(sourceMs)];
  if (exact) return exact;
  let best: string | null = null;
  let bestGap = 2000;
  Object.keys(frames).forEach((key) => {
    const gap = Math.abs(Number(key) - sourceMs);
    if (gap < bestGap) {
      bestGap = gap;
      best = frames[Number(key)];
    }
  });
  return best;
}
